import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { useSAAuth } from "../../contexts/SAAuthContext";
import { Save, MessageCircle, Tag, Pencil, Trash2, Plus, X, Check } from "lucide-react";

export default function SAConfig() {
  const { user } = useSAAuth();
  const isAdmin = user?.role === "Admin";

  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    meta_token: "",
    meta_phone_number_id: "",
    meta_webhook_verify_token: "",
  });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Tag catalog state
  const [tags, setTags] = useState([]);
  const [editingTag, setEditingTag] = useState(null); // { key, label, icon, display_style }
  const [newTag, setNewTag] = useState({ key: "", label: "", icon: "", display_style: "icon" });
  const [showAddTag, setShowAddTag] = useState(false);
  const [tagSaving, setTagSaving] = useState(false);

  const loadTags = async () => {
    try {
      const { data } = await api.get("/admin/tag-catalog");
      setTags(data.tags || []);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const load = async () => {
    const { data } = await api.get("/admin/config/whatsapp");
    setData(data);
    setForm({
      meta_token: "",
      meta_phone_number_id: data.meta_phone_number_id || "",
      meta_webhook_verify_token: "",
    });
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    loadTags();
  }, [isAdmin]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only include the secret fields if the user typed something — avoids
      // wiping the stored secret just because the masked field is empty.
      const body = {
        meta_phone_number_id: form.meta_phone_number_id,
      };
      if (form.meta_token.trim()) body.meta_token = form.meta_token.trim();
      if (form.meta_webhook_verify_token.trim())
        body.meta_webhook_verify_token = form.meta_webhook_verify_token.trim();
      const { data } = await api.put("/admin/config/whatsapp", body);
      setData(data);
      setForm({ ...form, meta_token: "", meta_webhook_verify_token: "" });
      toast.success("Global WhatsApp config saved");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const testConn = async () => {
    try {
      const { data } = await api.post("/admin/config/whatsapp/test");
      setTestResult(data);
      toast[data.ok ? "success" : "error"](data.message);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (!isAdmin) {
    return (
      <SALayout>
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-10 text-center">
          <h2 className="text-xl font-bold mb-2">Admin role required</h2>
          <p className="text-sm text-[#A0A0A0]">This page is only available to Superadmin admins.</p>
        </div>
      </SALayout>
    );
  }

  return (
    <SALayout>
      <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">Global Configuration</h1>
      <p className="text-sm text-[#A0A0A0] mb-6">Platform-level Meta WhatsApp Cloud API credentials used for every venue that doesn't have its own.</p>

      <form onSubmit={save} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 flex flex-col gap-5 mb-6">
        <div className="flex items-center gap-2">
          <MessageCircle size={16} className="text-[#9D00FF]" />
          <h2 className="text-lg font-bold">Global Meta WhatsApp Cloud Config</h2>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">TabOn Meta Access Token</label>
          <input
            data-testid="sa-cfg-meta-token"
            type="password"
            value={form.meta_token}
            onChange={(e) => setForm({ ...form, meta_token: e.target.value })}
            placeholder={data?.has_token ? `Current: ${data.meta_token_masked} — leave blank to keep` : "Paste Meta WhatsApp access token"}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
          />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">TabOn Meta Phone Number ID</label>
          <input
            data-testid="sa-cfg-meta-phone-number-id"
            value={form.meta_phone_number_id}
            onChange={(e) => setForm({ ...form, meta_phone_number_id: e.target.value })}
            placeholder="e.g. 1234567890123456"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
          />
          <p className="text-[11px] text-[#A0A0A0] mt-1">Found in Meta Business Manager → WhatsApp → Phone Numbers.</p>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">Meta Webhook Verify Token</label>
          <input
            data-testid="sa-cfg-meta-webhook-verify-token"
            type="password"
            value={form.meta_webhook_verify_token}
            onChange={(e) => setForm({ ...form, meta_webhook_verify_token: e.target.value })}
            placeholder={data?.meta_webhook_verify_token_masked ? `Current: ${data.meta_webhook_verify_token_masked} — leave blank to keep` : "Shared secret you configured in Meta webhook settings"}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 min-h-[44px] text-white focus:border-[#9D00FF]"
          />
          <p className="text-[11px] text-[#A0A0A0] mt-1">Meta sends this token on every webhook verify call. Must match exactly.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="sa-cfg-save"
            className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white font-semibold rounded-lg px-5 py-2.5 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={testConn}
            data-testid="sa-cfg-test"
            className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-5 py-2.5 text-sm font-semibold"
          >
            Test Global Connection
          </button>
        </div>

        {testResult && (
          <div
            data-testid="sa-cfg-test-result"
            className={`text-xs ${testResult.ok ? "text-emerald-300" : "text-[#EF4444]"}`}
          >
            {testResult.ok ? "✅ " : "⚠ "}{testResult.message}
          </div>
        )}
      </form>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Venues on Shared Number</div>
        <div className="text-3xl font-black text-white mt-1" data-testid="sa-cfg-shared-count">
          {data?.venues_on_shared ?? "—"}
        </div>
        <p className="text-xs text-[#A0A0A0] mt-1">
          Number of venues currently sending via TabOn's shared Meta WhatsApp number (no venue-level Meta Phone Number ID configured).
        </p>
      </div>

      {/* Tag Catalog Manager */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag size={16} className="text-[#9D00FF]" />
            <h2 className="text-lg font-bold">Menu Tag Catalog</h2>
          </div>
          <button
            type="button"
            onClick={() => { setShowAddTag(true); setNewTag({ key: "", label: "", icon: "", display_style: "icon" }); }}
            className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-semibold rounded-lg px-3 py-2"
          >
            <Plus size={12} /> Add Tag
          </button>
        </div>

        <p className="text-[11px] text-[#A0A0A0]">
          Platform-wide tags applied to menu items. Icon style renders the emoji inline; Pill style renders a text badge.
        </p>

        {/* Add new tag form */}
        {showAddTag && (
          <div className="bg-[#0A0A0A] border border-[#9D00FF]/30 rounded-lg p-4 flex flex-col gap-3">
            <div className="text-xs font-bold uppercase tracking-wider text-[#9D00FF]">New Tag</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Key (immutable)</label>
                <input
                  value={newTag.key}
                  onChange={e => setNewTag({ ...newTag, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                  placeholder="e.g. gluten_free"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Label</label>
                <input
                  value={newTag.label}
                  onChange={e => setNewTag({ ...newTag, label: e.target.value })}
                  placeholder="e.g. Gluten Free"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Icon (single emoji)</label>
                <input
                  value={newTag.icon}
                  onChange={e => setNewTag({ ...newTag, icon: e.target.value })}
                  placeholder="e.g. 🌾"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Display Style</label>
                <select
                  value={newTag.display_style}
                  onChange={e => setNewTag({ ...newTag, display_style: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                >
                  <option value="icon">Icon (emoji inline)</option>
                  <option value="pill">Pill (text badge)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={tagSaving}
                onClick={async () => {
                  if (!newTag.key || !newTag.label) { toast.error("Key and label are required"); return; }
                  setTagSaving(true);
                  try {
                    await api.post("/admin/tag-catalog", newTag);
                    toast.success("Tag created");
                    setShowAddTag(false);
                    await loadTags();
                  } catch (err) { toast.error(formatError(err)); }
                  finally { setTagSaving(false); }
                }}
                className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white text-xs font-semibold rounded-lg px-4 py-2"
              >
                <Check size={12} /> {tagSaving ? "Saving…" : "Save"}
              </button>
              <button type="button" onClick={() => setShowAddTag(false)}
                className="flex items-center gap-1.5 border border-white/10 text-[#A0A0A0] hover:text-white text-xs font-semibold rounded-lg px-4 py-2">
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Tag list */}
        <div className="flex flex-col divide-y divide-white/5">
          {tags.length === 0 && (
            <p className="text-sm text-[#A0A0A0] py-3">No tags yet.</p>
          )}
          {tags.map((t) => (
            <div key={t.key} className="py-3 first:pt-0 last:pb-0">
              {editingTag?.key === t.key ? (
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Label</label>
                      <input
                        value={editingTag.label}
                        onChange={e => setEditingTag({ ...editingTag, label: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Icon</label>
                      <input
                        value={editingTag.icon}
                        onChange={e => setEditingTag({ ...editingTag, icon: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1 block">Style</label>
                      <select
                        value={editingTag.display_style}
                        onChange={e => setEditingTag({ ...editingTag, display_style: e.target.value })}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF] focus:outline-none"
                      >
                        <option value="icon">Icon</option>
                        <option value="pill">Pill</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={tagSaving}
                      onClick={async () => {
                        setTagSaving(true);
                        try {
                          await api.put(`/admin/tag-catalog/${editingTag.key}`, editingTag);
                          toast.success("Tag updated");
                          setEditingTag(null);
                          await loadTags();
                        } catch (err) { toast.error(formatError(err)); }
                        finally { setTagSaving(false); }
                      }}
                      className="flex items-center gap-1 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white text-xs font-semibold rounded-lg px-3 py-1.5"
                    >
                      <Check size={11} /> {tagSaving ? "Saving…" : "Save"}
                    </button>
                    <button type="button" onClick={() => setEditingTag(null)}
                      className="flex items-center gap-1 border border-white/10 text-[#A0A0A0] hover:text-white text-xs font-semibold rounded-lg px-3 py-1.5">
                      <X size={11} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-lg w-7 text-center">{t.icon || "—"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{t.label}</div>
                    <div className="text-[10px] text-[#A0A0A0]">
                      key: <span className="font-mono">{t.key}</span> · {t.display_style}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingTag({ ...t })}
                    className="text-[#A0A0A0] hover:text-white"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`Delete tag "${t.label}"? This cannot be undone.`)) return;
                      try {
                        await api.delete(`/admin/tag-catalog/${t.key}`);
                        toast.success("Tag deleted");
                        await loadTags();
                      } catch (err) { toast.error(formatError(err)); }
                    }}
                    className="text-[#EF4444] hover:text-red-300"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SALayout>
  );
}
